/**
 * Creating a section, a group or a page.
 *
 * The id is derived from the title and shown while it is being typed, because it
 * is the one field here that is permanent — links, the agent's citations and
 * bookmarks all point at it — and the only chance to get it right is before the
 * thing exists. The server derives it again from the slug, so these are the same
 * rule written twice on purpose: one to show, one to trust.
 *
 * The same dialogs edit what they create. Everything here is changeable
 * afterwards except the id, which is shown but never editable — otherwise a typo
 * in a section title would be a reason to go back to a migration, which is the
 * whole thing this replaces.
 *
 * Refusals from the server are shown in the dialog rather than as a toast. They
 * are all about the form's own contents — an id already taken, a flag that does
 * not exist — so they belong next to the fields.
 */

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useGetAllFeatureFlags } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { IconLoader2 } from "@tabler/icons-react"
import { useState } from "react"
import {
  helpStructureRequests,
  useCreateHelpArticle,
  useHelpStructureMutation,
} from "../api/helpAdminApi"
import type {
  HelpAdminArticle,
  HelpAdminCategory,
  HelpAdminGroup,
} from "../api/types"
import { foldForSearch } from "../search"

/** "Erste Schritte" → "erste-schritte". Mirrors the server's slug rule. */
export function slugify(value: string): string {
  return foldForSearch(value)
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

function detail(error: unknown): string | null {
  const payload = (
    error as {
      response?: { data?: { detail?: { message?: string } | string } }
    }
  )?.response?.data?.detail
  if (typeof payload === "string") return payload
  return payload?.message ?? null
}

function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={htmlFor} className="text-xs text-grey5">
        {label}
      </Label>
      {children}
      {hint ? (
        <span className="font-mono text-[11px] text-white/35">{hint}</span>
      ) : null}
    </div>
  )
}

function FlagPicker({
  selected,
  onToggle,
}: {
  selected: string[]
  onToggle: (flag: string) => void
}) {
  const { translate } = useTranslation()
  const flags = useGetAllFeatureFlags()

  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-grey5">
        {translate("HELPDESK/MANAGE_FLAGS")}
      </Label>
      <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto rounded-md border border-white/8 p-2">
        {Object.keys(flags)
          .sort()
          .map((flag) => (
            <button
              key={flag}
              type="button"
              aria-pressed={selected.includes(flag)}
              onClick={() => onToggle(flag)}
              className={cn(
                "font-mono rounded-full border px-2 py-0.5 text-[11px] transition-colors",
                selected.includes(flag)
                  ? "border-yellow1/40 text-yellow1"
                  : "border-white/10 text-white/40 hover:text-white/70",
              )}
            >
              {flag}
            </button>
          ))}
      </div>
      <span className="text-[11px] text-white/35">
        {translate("HELPDESK/MANAGE_FLAGS_HINT")}
      </span>
    </div>
  )
}

function Shell({
  title,
  description,
  error,
  isPending,
  canSubmit,
  onSubmit,
  onClose,
  submitLabel,
  children,
}: {
  title: string
  description: string
  error: string | null
  isPending: boolean
  canSubmit: boolean
  onSubmit: () => void
  onClose: () => void
  submitLabel: string
  children: React.ReactNode
}) {
  const { translate } = useTranslation()
  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-M">{children}</div>

        {error ? (
          <p className="rounded-md border border-red1/30 bg-red1/10 px-3 py-2 text-xs text-white/80">
            {error}
          </p>
        ) : null}

        <DialogFooter>
          <Button
            variant="ghost"
            className="text-grey5 hover:text-white"
            onClick={onClose}
            disabled={isPending}
          >
            {translate("HELPDESK/EDITOR_CANCEL")}
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit || isPending}>
            {isPending ? (
              <IconLoader2 size={14} className="mr-1.5 animate-spin" />
            ) : null}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SectionDialog({
  lang,
  visualKeys,
  existing,
  onClose,
}: {
  lang: string
  visualKeys: string[]
  /** Omitted to create one; supplied to correct one that already exists. */
  existing?: HelpAdminCategory
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const label = existing?.labels?.de
  const [title, setTitle] = useState(label?.title ?? "")
  const [description, setDescription] = useState(label?.description ?? "")
  const [tag, setTag] = useState(label?.tag ?? "")
  const [visualKey, setVisualKey] = useState(
    existing?.visualKey ?? visualKeys[0] ?? "default",
  )
  const [flags, setFlags] = useState<string[]>(existing?.requiredFlags ?? [])
  const [error, setError] = useState<string | null>(null)
  const mutate = useHelpStructureMutation(lang)
  // Editing never re-derives the id from the title: it is permanent.
  const slug = existing ? existing.id : slugify(title)

  const submit = async () => {
    setError(null)
    const labels = { de: { title, description, tag } }
    try {
      await mutate.mutateAsync(
        existing
          ? helpStructureRequests.patchCategory(existing.id, {
              labels,
              visualKey,
              requiredFlags: flags,
            })
          : helpStructureRequests.createCategory({
              slug,
              labels,
              visualKey,
              requiredFlags: flags,
            }),
      )
      onClose()
    } catch (exception) {
      setError(detail(exception) ?? translate("HELPDESK/MANAGE_FAILED"))
    }
  }

  return (
    <Shell
      title={translate(
        existing
          ? "HELPDESK/MANAGE_EDIT_SECTION"
          : "HELPDESK/MANAGE_NEW_SECTION",
      )}
      description={translate(
        existing
          ? "HELPDESK/MANAGE_ID_IS_PERMANENT"
          : "HELPDESK/MANAGE_NEW_SECTION_HINT",
      )}
      error={error}
      isPending={mutate.isPending}
      canSubmit={Boolean(title.trim() && slug)}
      onSubmit={() => void submit()}
      onClose={onClose}
      submitLabel={translate(
        existing ? "HELPDESK/MANAGE_SAVE" : "HELPDESK/MANAGE_CREATE",
      )}
    >
      <Field
        label={translate("HELPDESK/EDITOR_TITLE")}
        htmlFor="section-title"
        hint={slug ? `/help/${slug}` : undefined}
      >
        <Input
          id="section-title"
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>
      <Field
        label={translate("HELPDESK/EDITOR_DESCRIPTION")}
        htmlFor="section-description"
      >
        <Textarea
          id="section-description"
          rows={2}
          value={description}
          maxLength={500}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      <Field label={translate("HELPDESK/MANAGE_TAG")} htmlFor="section-tag">
        <Input
          id="section-tag"
          value={tag}
          maxLength={40}
          onChange={(event) => setTag(event.target.value)}
        />
      </Field>
      <div className="flex flex-col gap-1.5">
        <Label className="text-xs text-grey5">
          {translate("HELPDESK/MANAGE_VISUAL")}
        </Label>
        <div className="flex flex-wrap gap-1.5">
          {visualKeys.map((key) => (
            <button
              key={key}
              type="button"
              aria-pressed={key === visualKey}
              onClick={() => setVisualKey(key)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs transition-colors",
                key === visualKey
                  ? "border-yellow1/40 text-yellow1"
                  : "border-white/10 text-white/40 hover:text-white/70",
              )}
            >
              {key}
            </button>
          ))}
        </div>
      </div>
      <FlagPicker
        selected={flags}
        onToggle={(flag) =>
          setFlags((current) =>
            current.includes(flag)
              ? current.filter((f) => f !== flag)
              : [...current, flag],
          )
        }
      />
    </Shell>
  )
}

export function GroupDialog({
  lang,
  categoryId,
  existing,
  onClose,
}: {
  lang: string
  categoryId: string
  existing?: HelpAdminGroup
  onClose: () => void
}) {
  const { translate } = useTranslation()
  const [title, setTitle] = useState(existing?.labels?.de?.title ?? "")
  const [error, setError] = useState<string | null>(null)
  const mutate = useHelpStructureMutation(lang)
  const slug = slugify(title)
  const id = existing ? existing.id : `${categoryId}:${slug}`

  const submit = async () => {
    setError(null)
    const labels = { de: { title } }
    try {
      await mutate.mutateAsync(
        existing
          ? helpStructureRequests.patchGroup(existing.id, { labels })
          : helpStructureRequests.createGroup({ categoryId, slug, labels }),
      )
      onClose()
    } catch (exception) {
      setError(detail(exception) ?? translate("HELPDESK/MANAGE_FAILED"))
    }
  }

  return (
    <Shell
      title={translate(
        existing ? "HELPDESK/MANAGE_EDIT_GROUP" : "HELPDESK/MANAGE_NEW_GROUP",
      )}
      description={translate(
        existing
          ? "HELPDESK/MANAGE_ID_IS_PERMANENT"
          : "HELPDESK/MANAGE_NEW_GROUP_HINT",
      )}
      error={error}
      isPending={mutate.isPending}
      canSubmit={Boolean(title.trim() && slug)}
      onSubmit={() => void submit()}
      onClose={onClose}
      submitLabel={translate(
        existing ? "HELPDESK/MANAGE_SAVE" : "HELPDESK/MANAGE_CREATE",
      )}
    >
      <Field
        label={translate("HELPDESK/EDITOR_TITLE")}
        htmlFor="group-title"
        hint={slug ? id : undefined}
      >
        <Input
          id="group-title"
          value={title}
          maxLength={120}
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>
    </Shell>
  )
}

export function NewPageDialog({
  lang,
  categoryId,
  groupId,
  onClose,
  onCreated,
}: {
  lang: string
  categoryId: string
  groupId: string
  onClose: () => void
  onCreated: (article: HelpAdminArticle) => void
}) {
  const { translate } = useTranslation()
  const [title, setTitle] = useState("")
  const [minutes, setMinutes] = useState(3)
  const [flags, setFlags] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const create = useCreateHelpArticle(lang)
  const slug = slugify(title)

  const submit = async () => {
    setError(null)
    try {
      const id = await create.mutateAsync({
        categoryId,
        groupId,
        slug,
        title,
        minutes,
        requiredFlags: flags,
      })
      // Straight into the editor: a page with no text is not finished work.
      onCreated({
        id,
        groupId,
        title,
        status: "draft",
        requiredFlags: flags,
        featured: false,
        needsReview: false,
        publishedLangs: [],
        draftLangs: [],
        helpfulCount: 0,
        notHelpfulCount: 0,
      })
    } catch (exception) {
      setError(detail(exception) ?? translate("HELPDESK/MANAGE_FAILED"))
    }
  }

  return (
    <Shell
      title={translate("HELPDESK/MANAGE_NEW_PAGE")}
      description={translate("HELPDESK/MANAGE_NEW_PAGE_HINT")}
      error={error}
      isPending={create.isPending}
      canSubmit={Boolean(title.trim() && slug)}
      onSubmit={() => void submit()}
      onClose={onClose}
      submitLabel={translate("HELPDESK/MANAGE_CREATE_AND_WRITE")}
    >
      <Field
        label={translate("HELPDESK/EDITOR_TITLE")}
        htmlFor="page-title"
        hint={slug ? `${categoryId}.${slug}` : undefined}
      >
        <Input
          id="page-title"
          value={title}
          maxLength={255}
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>
      <Field
        label={translate("HELPDESK/EDITOR_MINUTES")}
        htmlFor="page-minutes"
      >
        <Input
          id="page-minutes"
          type="number"
          min={1}
          max={120}
          value={minutes}
          onChange={(event) => setMinutes(Number(event.target.value) || 1)}
        />
      </Field>
      <FlagPicker
        selected={flags}
        onToggle={(flag) =>
          setFlags((current) =>
            current.includes(flag)
              ? current.filter((f) => f !== flag)
              : [...current, flag],
          )
        }
      />
    </Shell>
  )
}
